import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DatePicker from 'react-datepicker';
import { format } from 'date-fns';
import Select from 'react-select';
import { fetchPatients, selectPatients } from '../../../ReduxSlices/Patients/patientSlice';
import { fetchAllLabTests, selectLabTests } from '../../../ReduxSlices/LabTests/labTestSlice';
import LoadingDots from '../../shared/LoadingDots';
import { baseSelectStyles } from '../../shared/selectStyles';
import 'react-datepicker/dist/react-datepicker.css';
import '../../../stylesheets/PatientList.css';

const filterOptions = [
  { value: 'all', label: 'All Time' },
  { value: 'today', label: 'Today' },
  { value: 'month', label: 'This Month' },
  { value: 'year', label: 'This Year' },
  { value: 'custom', label: 'Specific Day' },
  { value: 'range', label: 'Date Range' },
];

const CustomInput = React.forwardRef(({ value, onClick, placeholder }, ref) => (
  <input
    ref={ref}
    value={value ? format(new Date(value), "do MMM, yyyy") : ""}
    onClick={onClick}
    readOnly
    placeholder={placeholder}
    className="date-picker-input"
    style={{
      backgroundColor: '#E9F7EE',
      border: '1px solid #A5D6A7',
      borderRadius: '8px',
      padding: '0 8px',
      height: '32px',
      fontSize: '13px',
      width: '150px',
      cursor: 'pointer',
    }}
  />
));

const PatientLabHistory = () => {
  const dispatch = useDispatch();
  const patients = useSelector(selectPatients);
  const allLabTests = useSelector(selectLabTests);
  const loading = useSelector((state) => state.labTests.loading);

  const [selectedPatientId, setSelectedPatientId] = useState('');

  const [filter, setFilter] = useState('all');
  const [customDate, setCustomDate] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  useEffect(() => {
    dispatch(fetchPatients());
    dispatch(fetchAllLabTests());
  }, [dispatch]);

  const selectedPatient = patients.find((p) => p.id === Number(selectedPatientId));

  const labTests = useMemo(() => {
    let tests = allLabTests;
    if (selectedPatientId) {
      tests = tests.filter((t) => t.patient?.id === Number(selectedPatientId));
    }
    const now = new Date();
    return tests.filter((test) => {
      const testDate = new Date(test.created_at);
      switch (filter) {
        case 'today':
          return testDate.toDateString() === now.toDateString();
        case 'month':
          return (
            testDate.getMonth() === now.getMonth() &&
            testDate.getFullYear() === now.getFullYear()
          );
        case 'year':
          return testDate.getFullYear() === now.getFullYear();
        case 'custom':
          if (!customDate) return true;
          return testDate.toDateString() === new Date(customDate).toDateString();
        case 'range':
          if (!startDate || !endDate) return false;
          const start = new Date(startDate);
          const end = new Date(endDate);
          end.setHours(23, 59, 59, 999);
          return testDate >= start && testDate <= end;
        default:
          return true;
      }
    });
  }, [allLabTests, selectedPatientId, filter, customDate, startDate, endDate]);


  const handlePrint = () => {
    if (!selectedPatientId) return;
    const params = new URLSearchParams({ patient_id: selectedPatientId });
    const now = new Date();

    switch (filter) {
      case 'today': {
        params.set('date', format(now, 'yyyy-MM-dd'));
        break;
      }
      case 'month': {
        const start = new Date(now.getFullYear(), now.getMonth(), 1);
        const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        params.set('start_date', format(start, 'yyyy-MM-dd'));
        params.set('end_date', format(end, 'yyyy-MM-dd'));
        break;
      }
      case 'year': {
        const start = new Date(now.getFullYear(), 0, 1);
        const end = new Date(now.getFullYear(), 11, 31);
        params.set('start_date', format(start, 'yyyy-MM-dd'));
        params.set('end_date', format(end, 'yyyy-MM-dd'));
        break;
      }
      case 'custom':
        if (customDate) params.set('date', format(customDate, 'yyyy-MM-dd'));
        break;
      case 'range':
        if (startDate) params.set('start_date', format(startDate, 'yyyy-MM-dd'));
        if (endDate) params.set('end_date', format(endDate, 'yyyy-MM-dd'));
        break;
    }

    window.open(`/api/v1/lab_tests/print_history?${params.toString()}`, '_blank');
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500 mt-1">View and print lab test records by patient</p>
        </div>
      </div>

      {/* Filters Card */}
      <div className="bg-white rounded-xl shadow-sm p-5">
        <div className="flex flex-wrap items-end gap-[30px] mb-[20px]">
          {/* Patient Search */}
          <div style={{ maxWidth: '240px', flex: '1 1 250px' }}>
           
            <Select
              value={
                selectedPatientId
                  ? { label: selectedPatient?.name, value: selectedPatient.id }
                  : null
              }
              options={patients.map((p) => ({ label: p.name, value: p.id }))}
              onChange={(option) => setSelectedPatientId(option?.value || '')}
              placeholder="Search patient..."
              isSearchable
              className="w-full"
              styles={baseSelectStyles}
              noOptionsMessage={() => "No patients found"}
            />
          </div>

          {/* Date Filter */}
          <div style={{ minWidth: '160px' }}>
              <Select
              value={filterOptions.find((o) => o.value === filter)}
              onChange={(o) => {
                setFilter(o.value);
                if (o.value !== 'custom') setCustomDate(null);
                if (o.value !== 'range') {
                  setStartDate(null);
                  setEndDate(null);
                }
              }}
              options={filterOptions}
              styles={baseSelectStyles}
              isSearchable={false}
            />
          </div>

          {/* Date Pickers */}
          {filter === 'custom' && (
            <div>
              <DatePicker
                selected={customDate}
                onChange={(date) => setCustomDate(date)}
                customInput={<CustomInput placeholder="Select date" />}
                placeholderText="Select date"
              />
            </div>
          )}

          {filter === 'range' && (
            <div className="flex items-end gap-[20px]">
              <div>
                
                <DatePicker
                  selected={startDate}
                  onChange={(date) => setStartDate(date)}
                  selectsStart
                  startDate={startDate}
                  endDate={endDate}
                  customInput={<CustomInput placeholder="Start date" />}
                  placeholderText="Start date"
                />
              </div>
              <div>
                <DatePicker
                  selected={endDate}
                  onChange={(date) => setEndDate(date)}
                  selectsEnd
                  startDate={startDate}
                  endDate={endDate}
                  minDate={startDate}
                  customInput={<CustomInput placeholder="End date" />}
                  placeholderText="End date"
                />
              </div>
            </div>
          )}


        </div>
      </div>

      {/* Content */}
      {!selectedPatient ? (
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm py-20 text-center">
          <p className="text-gray-500 text-lg">Select a patient to view lab test history</p>
        </div>
      ) : loading ? (
        <div className="flex justify-center py-16">
          <LoadingDots height={12} width={40} color="#38AD3E" />
        </div>
      ) : (
        <>
          {/* Patient Info + Stats */}
          <div className="bg-white border rounded-xl shadow-sm p-5">
            <div className="flex items-center gap-4 mb-5 pb-4 border-b border-gray-100">
              <span className="w-12 h-12 rounded-full bg-green-100 text-green-700 flex items-center justify-center text-lg font-bold">
                {selectedPatient.name.charAt(0).toUpperCase()}
              </span>
              <div>
                <h2 className="text-lg font-bold text-gray-800">{selectedPatient.name}</h2>
                <p className="text-sm text-gray-500">{selectedPatient.contact_info || 'No contact info'}</p>
              </div>
            </div>
         
           </div>

          {/* Table */}
          {labTests.length === 0 ? (
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm py-16 text-center">
              <p className="text-gray-400 text-lg">No lab tests match the current filters</p>
            </div>
          ) : (
            <div className="bg-white border rounded-xl shadow-sm overflow-hidden">
              <table className="list-table w-full border-collapse">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-5 py-3.5 text-left text-xs font-semibold uppercase tracking-wider border-b border-gray-200">Date</th>
                    <th className="px-5 py-3.5 text-left text-xs font-semibold uppercase tracking-wider border-b border-gray-200">Test Type</th>
                    <th className="px-5 py-3.5 text-left text-xs font-semibold uppercase tracking-wider border-b border-gray-200">Status</th>
                    <th className="px-5 py-3.5 text-left text-xs font-semibold uppercase tracking-wider border-b border-gray-200">Requested By</th>
                    <th className="px-5 py-3.5 text-center text-xs font-semibold uppercase tracking-wider border-b border-gray-200">Print</th>
                  </tr>
                </thead>
                <tbody>
                  {labTests.map((test, idx) => (
                    <tr
                      key={test.id}
                      className={`hover:bg-green-50 transition-colors ${
                        idx !== labTests.length - 1 ? 'border-b border-gray-100' : ''
                      }`}
                    >
                      <td className="px-5 py-3.5 text-sm text-gray-600 whitespace-nowrap">
                        {new Date(test.created_at).toLocaleString('en-GB', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </td>
                      <td className="px-5 py-3.5 text-sm font-medium text-gray-800">
                        {test.lab_test_type_name}
                      </td>
                      <td className="px-5 py-3.5">
                        <span
                          className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
                            test.status === 'completed'
                              ? 'bg-green-100 text-green-800'
                              : test.status === 'pending'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {test.status.charAt(0).toUpperCase() + test.status.slice(1)}
                        </span>
                      </td>
                    <td className="px-5 py-3.5 text-sm text-gray-600 capitalize">
                        {test.requested_by_user_name || test.requested_by || '-'}
                      </td>
                      <td className="px-5 py-3.5 text-center">
                        <button
                          type="button"
                          onClick={() => handlePrint()}
                          className="p-1.5 text-green-600 hover:text-white hover:bg-green-600 rounded transition-colors"
                          title="Print"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PatientLabHistory;
